import { Formik } from "formik";
import * as Yup from "yup";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  useAddMessageDraft,
  useUpdateMessageDraft,
  useDeleteMessageDraft,
} from "../../../../../reactQueryHooks";
import BarLoader from "react-spinners/BarLoader";
import { useFetchEntities } from "../../../../../reactQueryHooks";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";

//---------------------------------------------------------

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;

const Modal = styled(motion.div)`
  background-color: white;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 10px;
  gap: 5px;
  width: 90%;
  max-width: 500px;
  max-height: 90%;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const CloseContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const CloseButton = styled.button`
  background-color: whitesmoke;
  border: none;
  color: white;
  height: 30px;
  width: 30px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  i {
    color: black;
    font-size: 15px;
  }
  &:hover {
    i {
      color: indianred;
    }
  }
`;
const RowsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 5px;
  padding: 0 5px 0 0;
`;

const CustomButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  height: 40px;
  border: none;
  border-radius: 4px;
  background-color: #b4b4b4;
  color: white;
  padding: 0 8px;
  cursor: pointer;
  font-size: 13px;
  &:hover {
    filter: brightness(1.1);
  }
  &:focus {
    outline: none;
  }
  .spinner {
    animation: spin 1s linear infinite;
    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
  width: 100%;
`;

//---------------------------------------------------------

const AddMessageDraftModal = ({
  close,
  data,
}: {
  close: () => void;
  data: any;
}) => {
  const {
    refetch,
  }: {
    refetch: () => void;
  } = useFetchEntities();

  const { mutateAsync: addNewDraft, isLoading: isAddingNewUser } =
    useAddMessageDraft();

  const { mutateAsync: updateDraft, isLoading: isUpdatingUser } =
    useUpdateMessageDraft();

  const { mutateAsync: deleteDraft, isLoading: isDeletingUser } =
    useDeleteMessageDraft();

  return (
    <Formik
      validateOnMount
      enableReinitialize
      initialValues={{
        title: data?.title || "",
        body: data?.body || "",
      }}
      validationSchema={Yup.object({
        title: Yup.string().required("Title is required"),
        body: Yup.string().required("Body is required"),
      })}
      onSubmit={async (values, { setSubmitting, setFieldValue }) => {
        try {
          if (data._id) {
            const response = await updateDraft({
              id: data._id,
              payload: values,
            } as any);
            //@ts-ignore
            alert(response?.data);
          } else {
            const response = await addNewDraft(values as any);
            //@ts-ignore
            alert(response?.data);
          }

          refetch();
          close();
        } catch (err) {
          //@ts-ignore
          if (err.response.data) {
            //@ts-ignore
            alert(err.response.data);
          } else {
            //@ts-ignore
            alert(err.toString());
          }
          console.log(err);
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        resetForm,
        isSubmitting,
        validateForm,
      }) => {
        return (
          <Wrapper>
            <Modal
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                opacity: { duration: 0.2 },
                scale: {
                  //type: "spring",
                  // stiffness: 300, // Adjust stiffness for more/less bounce
                  // damping: 20, // Adjust damping for faster/slower settle
                  // duration: 0.02,
                },
              }}
            >
              <CloseContainer>
                <CloseButton onClick={close}>
                  <i className="fas fa-times"></i>
                </CloseButton>
              </CloseContainer>
              <ContentContainer>
                <RowsContainer>
                  <TextField
                    style={{ flex: 1, minWidth: 200 }}
                    name={"title"}
                    label={"Title"}
                    margin="dense"
                    type="text"
                    variant="filled"
                    color="success"
                    value={values.title}
                    onChange={handleChange}
                    error={Boolean(errors.title)}
                  />
                  <div
                    style={{
                      display: "flex",
                      flex: 1,
                      minWidth: 200,
                      gap: 5,
                      //wrap content
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setFieldValue("body", values.body + " [NAME] ");
                      }}
                    >
                      CLIENT NAME
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setFieldValue("body", values.body + " [TOUR_DATE] ");
                      }}
                    >
                      DATE
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setFieldValue("body", values.body + " [TOUR_NAME] ");
                      }}
                    >
                      TOUR TITLE
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setFieldValue(
                          "body",
                          values.body + " [MEETING_POINT] "
                        );
                      }}
                    >
                      MEETING POINT
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setFieldValue("body", values.body + " [MEETING_TIME] ");
                      }}
                    >
                      MEETING TIME
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setFieldValue("body", values.body + " [INSTRUCTIONS] ");
                      }}
                    >
                      INSTRUCTIONS
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setFieldValue("body", values.body + " [MAP_URL] ");
                      }}
                    >
                      MAP URL
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setFieldValue("body", values.body + " [TOUR_PAGE] ");
                      }}
                    >
                      PORTAL URL
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setFieldValue("body", values.body + " [AGENT_NAME] ");
                      }}
                    >
                      AGENT NAME
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setFieldValue("body", values.body + " [ADDRESS] ");
                      }}
                    >
                      ADDRESS
                    </Button>
                  </div>
                  <TextField
                    style={{ flex: 1, minWidth: 200 }}
                    name={"body"}
                    label={"Draft Body"}
                    margin="dense"
                    type="text"
                    color="success"
                    value={values.body}
                    onChange={handleChange}
                    multiline
                    maxRows={Infinity}
                    error={Boolean(errors.body)}
                  />
                </RowsContainer>
              </ContentContainer>
              <ButtonWrapper>
                <CustomButton
                  style={{
                    backgroundColor: "#6eafbb",
                  }}
                  onClick={() => {
                    resetForm();
                  }}
                >
                  CLEAR
                </CustomButton>
                <CustomButton
                  style={{
                    backgroundColor: "#7ab87d",
                    flex: 4,
                  }}
                  title="Submit"
                  type="submit"
                  onClick={() => {
                    if (Object.keys(errors).length > 0) {
                      validateForm();
                      const errorsString = Object.values(errors).join("\n");
                      alert(errorsString);
                    } else {
                      if (
                        window.confirm(
                          "Submit ? \nPlease make sure all the data is correct"
                        )
                      ) {
                        handleSubmit();
                      }
                    }
                  }}
                >
                  {isAddingNewUser || isUpdatingUser ? (
                    <BarLoader color="#606060" loading={true} />
                  ) : (
                    "SUBMIT"
                  )}
                </CustomButton>
              </ButtonWrapper>
              {data?._id && (
                <ButtonWrapper>
                  <CustomButton
                    onClick={async () => {
                      try {
                        if (
                          window.confirm(
                            "Are you sure you wish to delete this item?"
                          )
                        ) {
                          const response = await deleteDraft(data?._id);
                          //@ts-ignore
                          alert(response?.data);
                          refetch();
                          close();
                        }
                      } catch (error) {
                        alert(error?.toString());
                      }
                    }}
                    style={{ backgroundColor: "rgb(185,119,119)" }}
                  >
                    {isDeletingUser ? (
                      <BarLoader color="#606060" loading={true} />
                    ) : (
                      <i className="fas fa-trash"></i>
                    )}
                  </CustomButton>
                </ButtonWrapper>
              )}
            </Modal>
          </Wrapper>
        );
      }}
    </Formik>
  );
};

export default AddMessageDraftModal;
